import { useState, useEffect, ComponentType } from 'react';
import { motion } from 'framer-motion';
import {
  PencilIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import type { ContentItem } from '../types/content';

interface ContentSection {
  id: string;
  title: string;
  icon: ComponentType<{ className?: string }>;
  items: ContentItem[];
}

interface SectionConfig {
  title: string;
  icon: ComponentType<{ className?: string }>;
}

interface ContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (item: ContentItem) => void;
  item: ContentItem | null;
  sectionId: string;
  sections?: ContentSection[];
}

interface ContentSectionManagerProps {
  page: string;
  initialSections: SectionConfig[];
  sectionTitleFilter?: string;
  ModalComponent: ComponentType<ContentModalProps>;
  serializeContentOnEdit?: boolean;
  itemLabel?: string;
}

export default function ContentSectionManager({
  page,
  initialSections,
  sectionTitleFilter,
  ModalComponent,
  serializeContentOnEdit = true,
  itemLabel = 'items',
}: ContentSectionManagerProps) {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [sections, setSections] = useState<ContentSection[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ContentItem | null>(null);
  const [selectedSection, setSelectedSection] = useState<string>('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ sectionId: string; item: ContentItem } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);

      for (const section of initialSections) {
        const { data: existingSection, error: checkError } = await supabase
          .from('content_sections')
          .select('id')
          .eq('page', page)
          .eq('title', section.title)
          .maybeSingle();

        if (checkError) throw checkError;

        if (!existingSection) {
          const { error: insertError } = await supabase
            .from('content_sections')
            .insert([{
              page,
              title: section.title,
              icon: section.icon.name || 'RocketLaunchIcon'
            }]);
          if (insertError) throw insertError;
        }
      }

      const sectionTitles = initialSections.map(s => s.title);

      let query = supabase
        .from('content_sections')
        .select(`*, content_items (*)`)
        .eq('page', page)
        .in('title', sectionTitleFilter ? [sectionTitleFilter] : sectionTitles);

      const { data: dbSections, error } = await query;
      if (error) throw error;

      if (dbSections) {
        const processedSections = dbSections.map(dbSection => {
          const matchingInitial = initialSections.find(s => s.title === dbSection.title);
          return {
            id: dbSection.id,
            title: dbSection.title,
            icon: matchingInitial?.icon || RocketLaunchIcon,
            items: (dbSection.content_items || []).map((item: any) => ({
              ...item,
              lastUpdated: new Date(item.updated_at || Date.now()).toISOString().split('T')[0]
            }))
          };
        });
        setSections(processedSections);

        if (!expandedSection && processedSections.length > 0 && processedSections[0]) {
          setExpandedSection(processedSections[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching content:', err);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  const handleEditClick = (sectionId: string, item: ContentItem) => {
    const editItem = serializeContentOnEdit
      ? { ...item, content: typeof item.content === 'string' ? item.content : JSON.stringify(item.content) }
      : item;
    setSelectedItem(editItem);
    setSelectedSection(sectionId);
    setIsModalOpen(true);
  };

  const handleAddClick = (sectionId: string) => {
    setSelectedItem(null);
    setSelectedSection(sectionId);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (sectionId: string, item: ContentItem) => {
    setItemToDelete({ sectionId, item });
    setIsDeleteModalOpen(true);
  };

  const handleSave = async (item: ContentItem) => {
    try {
      let parsedContent: any;
      try {
        parsedContent = JSON.parse(item.content);
      } catch {
        parsedContent = item.content;
      }

      const itemData = {
        title: item.title,
        type: item.type,
        content: parsedContent,
        status: item.status,
        section_id: selectedSection
      };

      if (selectedItem?.id) {
        const { error } = await supabase
          .from('content_items')
          .update(itemData)
          .eq('id', selectedItem.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('content_items')
          .insert([itemData]);
        if (error) throw error;
      }

      await fetchContent();
      setIsModalOpen(false);
      toast.success('Content saved successfully');
    } catch (err: any) {
      console.error('Error saving content:', err);
      toast.error(err.message || 'Failed to save content');
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete?.item.id) return;

    try {
      const { error } = await supabase
        .from('content_items')
        .delete()
        .eq('id', itemToDelete.item.id);
      if (error) throw error;

      await fetchContent();
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      toast.success('Content deleted successfully');
    } catch (err) {
      console.error('Error deleting content:', err);
      toast.error('Failed to delete content');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {sections.map((section) => (
        <motion.div
          key={section.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
        >
          <div
            className="flex items-center justify-between p-6 cursor-pointer"
            onClick={() => toggleSection(section.id)}
          >
            <div className="flex items-center space-x-4">
              <div className="bg-golf-orange/10 p-3 rounded-xl">
                <section.icon className="w-6 h-6 text-golf-orange" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-golf-gray">{section.title}</h3>
                <p className="text-sm text-gray-500">{section.items.length} {itemLabel}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddClick(section.id);
                }}
                className="p-2 text-golf-orange hover:bg-golf-orange/10 rounded-lg transition-colors"
              >
                <PlusIcon className="w-5 h-5" />
              </button>
              {expandedSection === section.id ? (
                <ChevronUpIcon className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
              )}
            </div>
          </div>

          {expandedSection === section.id && (
            <div className="border-t border-gray-200 p-4 space-y-3">
              {section.items.map((item) => (
                <div
                  key={item.id}
                  className="group border border-gray-200 rounded-lg hover:border-golf-orange/40 hover:shadow-sm transition-all"
                >
                  <div className="flex items-start justify-between p-4 gap-4">
                    <div
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => handleEditClick(section.id, item)}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-sm font-semibold text-golf-gray truncate">{item.title}</h4>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(section.id, item);
                        }}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <PencilIcon className="w-4.5 h-4.5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(section.id, item);
                        }}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <TrashIcon className="w-4.5 h-4.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {section.items.length === 0 && (
                <div className="text-center py-8 text-gray-400 text-sm">
                  No items yet. Click the + button above to add one.
                </div>
              )}
            </div>
          )}
        </motion.div>
      ))}

      <ModalComponent
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        item={selectedItem}
        sectionId={selectedSection}
        sections={sections}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title={itemToDelete?.item.title || ''}
      />
    </div>
  );
}
